import { Button, Row, Col } from "reactstrap";
import React, { Fragment, useEffect, useState } from "react";
import ModalFrame from "../../../toolkit/modalFrame";
import { searchDonorById } from "../../../utils/fetch";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import { ScaleLoader } from "react-spinners";
import "firebase/compat/auth";
import classNames from "classnames";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import IThankYouInfo from "../../../types/services/IThankYouInfo";
import IUser from "../../../types/services/IUser";
import {
  isDonorDeactivated,
  donorHasNotifications,
} from "../../../utils/helpers";

export enum PersonalViewSparkType {
  ThankYou,
  Personalized,
}

interface Props extends ServiceCallerProps {
  modalType: PersonalViewSparkType;
  thankYouInfos: Array<IThankYouInfo>;
  isOpen: boolean;
  toggle: () => void;
  goAction?: () => void;
  dismissAction?: () => void;
  skipOkConfirmation: boolean;
}

enum Message {
  Initial,
  NoChannels,
  SimpleConfirmation,
  PersonalSent,
  GroupalSent,
}

function ThankYouModal(props: Props) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(Message.Initial);

  useEffect(() => {
    if (!props.isOpen) {
      setMessage(Message.Initial);
      setLoading(false);
      return;
    }
    if (props.thankYouInfos.length !== 1) {
      setLoading(false);
      return;
    }

    const thankYouInfo = props.thankYouInfos[0];
    const donorId = thankYouInfo.donor?.id;
    if (!donorId || !props.config?.adminServiceUrl) {
      setLoading(false);
      setMessage(Message.NoChannels);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setMessage(Message.Initial);

    searchDonorById(
      props.config.adminServiceUrl,
      props.currentCharity.id,
      donorId,
      props.serviceAccessToken
    )
      .then(response => {
        if (cancelled) return;
        setLoading(false);
        if (!donorHasNotifications(response)) {
          setMessage(Message.NoChannels);
          return;
        }
        if (!thankYouInfo.thankYouLogs) {
          setMessage(Message.SimpleConfirmation);
          return;
        }
        const personalSent = thankYouInfo.thankYouLogs.some(
          thankYouLog => thankYouLog.type == "personal"
        );
        setMessage(personalSent ? Message.PersonalSent : Message.GroupalSent);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
        setMessage(Message.NoChannels);
      });

    return () => {
      cancelled = true;
    };
  }, [
    props.isOpen,
    props.thankYouInfos,
    props.config?.adminServiceUrl,
    props.currentCharity.id,
    props.serviceAccessToken,
  ]);

  useEffect(() => {
    if (
      !loading &&
      props.skipOkConfirmation &&
      message === Message.SimpleConfirmation
    ) {
      props.toggle();
      if (props.goAction) props.goAction();
    }
  }, [loading, message, props.skipOkConfirmation]);

  const toggle = () => {
    setMessage(Message.Initial);
    props.toggle();
  };

  const goAction = () => {
    toggle();
    if (props.goAction) props.goAction();
  };

  const dismissAction = () => {
    toggle();
    if (props.dismissAction) props.dismissAction();
  };

  const typeMessage = (modalType: PersonalViewSparkType): string => {
    if (modalType == PersonalViewSparkType.Personalized) return "Personalized";
    else return "Thank You";
  };

  const auxMessage = (modalType: PersonalViewSparkType): string => {
    if (modalType == PersonalViewSparkType.Personalized) return "";
    else return " thank";
  };

  const name = (thankYouInfo: IThankYouInfo): string => {
    return thankYouInfo.donor.name ? thankYouInfo.donor.name : "this contact";
  };

  const renderButtons = (isOnlyAccept: boolean) => {
    return (
      <div className={s.centered}>
        {!isOnlyAccept && (
          <Row>
            <Col xs={1}></Col>
            <Col xs={5}>
              <Button onClick={dismissAction} className={classNames(s.button)}>
                Cancel
              </Button>
            </Col>
            <Col xs={5}>
              <Button onClick={goAction} className={classNames(s.button)}>
                Accept
              </Button>
            </Col>
            <Col xs={1}></Col>
          </Row>
        )}

        {isOnlyAccept && (
          <Row>
            <Col xs={2}></Col>
            <Col xs={8}>
              <Button onClick={dismissAction} className={classNames(s.button)}>
                Accept
              </Button>
            </Col>
            <Col xs={2}></Col>
          </Row>
        )}
      </div>
    );
  };

  const renderGroupThankYouContent = () => {
    const donors = [],
      deactivatedDonors = [],
      noNotifications = [];

    props.thankYouInfos.forEach(thankYouInfo => {
      const donor = thankYouInfo.donor;
      if (isDonorDeactivated(donor)) deactivatedDonors.push(donor);
      else if (!donorHasNotifications(donor)) noNotifications.push(donor);
      else donors.push(donor);
    });

    const getNameList = (donorList: Array<IUser>) =>
      donorList.map(donor => {
        return <li key={donor.id}>{donor.name ? donor.name : "No name"}</li>;
      });

    const donorList = getNameList(donors);
    const deactivatedList = getNameList(deactivatedDonors);
    const noNotificationsList = getNameList(noNotifications);
    const typeMsg = typeMessage(props.modalType),
      auxMsg = auxMessage(props.modalType);

    return (
      <Fragment>
        <div className={s.marginBot}>
          {donorList.length > 0 && (
            <Fragment>
              <div>
                Do you want to create a {typeMsg} Viewspark to{auxMsg}:
              </div>
              <ul>{donorList}</ul>
            </Fragment>
          )}
          {donorList.length == 0 && (
            <div className={s.marginBot}>
              No donors are available to send the {typeMsg} Viewspark.
            </div>
          )}
          {deactivatedList.length > 0 && (
            <Fragment>
              <div>The following contacts are deactivated:</div>
              <ul>{deactivatedList}</ul>
            </Fragment>
          )}
          {noNotificationsList.length > 0 && (
            <Fragment>
              <div>The following contacts have no available channel:</div>
              <ul>{noNotificationsList}</ul>
            </Fragment>
          )}
        </div>
        {renderButtons(donorList.length == 0)}
      </Fragment>
    );
  };

  const renderPersonalThankYouContent = () => {
    const thankYouInfo = props.thankYouInfos[0];
    if (message === Message.Initial || loading) {
      return null;
    }

    let messageContent,
      buttons = renderButtons(false);
    const typeMsg = typeMessage(props.modalType),
      auxMsg = auxMessage(props.modalType),
      donorName = name(thankYouInfo);

    if (message == Message.PersonalSent) {
      messageContent =
        `Do you want to create another Thank You ViewSpark to thank ` +
        `${donorName} for the donation of ${thankYouInfo.amountPretty}?`;
    } else if (message == Message.GroupalSent) {
      messageContent =
        `Do you want to create another Thank You ViewSpark (A groupal has ` +
        `already been sent) to thank ${donorName} for the donation of ${thankYouInfo.amountPretty}?`;
    } else if (message == Message.NoChannels) {
      messageContent =
        `This contact doesn't have any available channel to ` +
        `send the ${typeMsg} ViewSpark.`;
      buttons = renderButtons(true);
    } else {
      messageContent =
        `Do you want to create a ${typeMsg} ViewSpark to${auxMsg} ${donorName}` +
        (thankYouInfo.amountPretty
          ? ` for the donation of ${thankYouInfo.amountPretty}`
          : "") +
        "?";
    }

    return (
      <Fragment>
        <div className={classNames(s.centered, s.marginBot)}>
          {messageContent}
        </div>
        {buttons}
      </Fragment>
    );
  };

  return (
    <ModalFrame
      isOpen={props.isOpen && (!props.skipOkConfirmation || !loading)}
      toggle={toggle}
      size="xl"
      title={`Create a ${typeMessage(props.modalType)} Viewspark`}
      subtitle={""}
    >
      <div className={s.centered}>
        <ScaleLoader color={"#1f65b1"} loading={loading} />
      </div>
      {!loading && props.thankYouInfos && (
        <Fragment>
          {props.thankYouInfos.length > 1 && renderGroupThankYouContent()}
          {props.thankYouInfos.length == 1 && renderPersonalThankYouContent()}
        </Fragment>
      )}
    </ModalFrame>
  );
}

export default serviceCaller(withStyles(s)(ThankYouModal));
